import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';

const TOKEN = () => localStorage.getItem('crm_token');
const HEADERS = () => ({ Authorization: `Bearer ${TOKEN()}`, 'Content-Type': 'application/json' });

// ─── Entity Definitions ──────────────────────────────────────────────────────

const ENTITIES = [
  {
    id: 'customers',
    label: 'Müşteriler',
    icon: '👥',
    color: '#6366f1',
    desc: 'Müşteri listesi ve iletişim bilgileri',
    fields: [
      { key: 'id',                label: 'Müşteri ID (Opsiyonel)', required: false },
      { key: 'first_name',        label: 'Ad',                 required: true },
      { key: 'last_name',         label: 'Soyad',              required: false },
      { key: 'phone',             label: 'Telefon',            required: true },
      { key: 'email',             label: 'E-posta',            required: false },
      { key: 'gender',            label: 'Cinsiyet (male/female)', required: false },
      { key: 'birth_date',        label: 'Doğum Tarihi',       required: false },
      { key: 'address',           label: 'Adres',              required: false },
      { key: 'city',              label: 'Şehir',              required: false },
      { key: 'notes',             label: 'Notlar',             required: false },
      { key: 'source',            label: 'Kaynak',             required: false },
      { key: 'instagram_id',      label: 'Instagram',          required: false },
      { key: 'whatsapp_phone',    label: 'WhatsApp',           required: false },
      { key: 'preferred_channel', label: 'Tercih Edilen Kanal',required: false },
    ],
  },
  {
    id: 'services',
    label: 'Hizmetler',
    icon: '💇',
    color: '#ec4899',
    desc: 'Sunulan hizmetler ve fiyat listesi',
    fields: [
      { key: 'id',           label: 'Hizmet ID (Opsiyonel)', required: false },
      { key: 'name',         label: 'Hizmet Adı',  required: true },
      { key: 'duration_min', label: 'Süre (dk)',    required: true },
      { key: 'price',        label: 'Fiyat (₺)',    required: true },
      { key: 'description',  label: 'Açıklama',    required: false },
      { key: 'color_code',   label: 'Renk Kodu',   required: false },
    ],
  },
  {
    id: 'products',
    label: 'Ürünler',
    icon: '📦',
    color: '#10b981',
    desc: 'Stok ürünleri ve satış fiyatları',
    fields: [
      { key: 'id',             label: 'Ürün ID (Opsiyonel)',   required: false },
      { key: 'name',           label: 'Ürün Adı',      required: true },
      { key: 'sale_price',     label: 'Satış Fiyatı',  required: true },
      { key: 'cost_price',     label: 'Maliyet Fiyatı',required: false },
      { key: 'stock_quantity', label: 'Stok Miktarı',  required: false },
      { key: 'brand',          label: 'Marka',         required: false },
      { key: 'sku',            label: 'SKU / Barkod',  required: false },
    ],
  },
  {
    id: 'appointments',
    label: 'Randevular',
    icon: '📅',
    color: '#f59e0b',
    desc: 'Müşteri randevuları ve takvim verileri',
    fields: [
      { key: 'id',             label: 'Randevu ID (Opsiyonel)',                      required: false },
      { key: 'scheduled_at',   label: 'Randevu Tarih+Saat (tek kolon)',               required: false },
      { key: 'scheduled_date', label: 'Randevu Tarihi (ayrı kolon — Tarih)',           required: false },
      { key: 'scheduled_time', label: 'Randevu Saati (ayrı kolon — Saat)',            required: false },
      { key: 'customer_name',  label: 'Müşteri Adı (otomatik oluşturma için)',          required: false },
      { key: 'customer_id',    label: 'Müşteri ID (Alternatif)',                       required: false },
      { key: 'customer_phone', label: 'Müşteri Telefonu (★ eşleştirme için zorunlu)',    required: false },
      { key: 'service_id',     label: 'Hizmet ID (Alternatif)',                       required: false },
      { key: 'service_name',   label: 'Hizmet Adı (birden fazlaysa ilki alınır)',       required: false },
      { key: 'staff_id',       label: 'Personel ID (Alternatif)',                     required: false },
      { key: 'staff_name',     label: 'Personel Adı',                                 required: false },
      { key: 'status',         label: 'Durum (Onaylandı/İptal/Tamamlandı)',           required: false },
      { key: 'notes',          label: 'Notlar',                                       required: false },
    ],
  },
];

// ─── Auto-match aliases ───────────────────────────────────────────────────────

const ALIASES = {
  id:                ['id', 'no', 'numara', 'kod', 'müşteri id', 'musteri id', 'randevu id', 'hizmet id', 'ürün id', 'urun id'],
  customer_id:       ['müşteri id', 'musteri id', 'customer id', 'customer_id', 'musterino', 'müşterino'],
  service_id:        ['hizmet id', 'hizmet_id', 'service id', 'service_id', 'hizmetno'],
  staff_id:          ['personel id', 'personel_id', 'staff id', 'staff_id', 'personelno', 'calisanno', 'çalışanno'],
  first_name:        ['ad', 'isim', 'firstname', 'first_name', 'name', 'müşteri adı', 'müşteri'],
  last_name:         ['soyad', 'soyadı', 'lastname', 'last_name', 'surname'],
  phone:             ['telefon', 'tel', 'gsm', 'cep', 'mobile', 'phone', 'phonenumber', 'telefon numarası', 'phone_number'],
  email:             ['email', 'eposta', 'e-posta', 'mail'],
  gender:            ['cinsiyet', 'gender', 'sex'],
  birth_date:        ['doğum', 'dogum', 'birth', 'birthdate', 'birth_date', 'doğum tarihi'],
  address:           ['adres', 'address', 'konum'],
  city:              ['şehir', 'sehir', 'city', 'il'],
  notes:             ['not', 'notlar', 'notes', 'açıklama', 'aciklama'],
  source:            ['kaynak', 'source'],
  instagram_id:      ['instagram', 'ig', 'instagramid', 'ig_account_name'],
  whatsapp_phone:    ['whatsapp', 'wp', 'whatsappno'],
  preferred_channel: ['kanal', 'channel'],
  name:              ['ad', 'isim', 'name', 'hizmet adı', 'ürün adı', 'product', 'service'],
  duration_min:      ['süre', 'sure', 'duration', 'duration_min', 'dk', 'dakika', 'minute'],
  price:             ['fiyat', 'price', 'ücret', 'ucret'],
  sale_price:        ['satış fiyatı', 'satis fiyati', 'sale', 'sale_price', 'satış'],
  cost_price:        ['maliyet', 'cost', 'cost_price'],
  stock_quantity:    ['stok', 'stock', 'quantity', 'adet', 'miktar'],
  brand:             ['marka', 'brand'],
  sku:               ['sku', 'barkod', 'barcode', 'kod'],
  // Appointments
  scheduled_at:      ['tarih saat', 'tarih ve saat', 'scheduled_at', 'randevu tarihi', 'datetime'],
  scheduled_date:    ['tarih', 'date', 'randevu tarihi', 'gün'],
  scheduled_time:    ['saat', 'time', 'randevu saati'],
  customer_name:     ['müşteri', 'müşteri adı', 'customer name', 'customer_name', 'adı soyadı'],
  customer_phone:    ['müşteri tel', 'musteri tel', 'customer phone', 'customer_phone', 'telefon numarası', 'telefon'],
  service_name:      ['hizmetler', 'hizmet', 'service', 'hizmet adı', 'service_name', 'services'],
  staff_name:        ['personel', 'staff', 'staff_name', 'çalışan', 'oluşturan'],
  status:            ['durum', 'status'],
};

function autoMatch(csvCols, entityFields) {
  const mapping = {};
  entityFields.forEach(f => {
    const dbKey = f.key;
    const aliases = ALIASES[dbKey] || [dbKey];
    for (const col of csvCols) {
      const norm = col.toLowerCase().trim().replace(/\s+/g, ' ');
      if (aliases.some(a => a === norm || norm.includes(a) || a.includes(norm))) {
        mapping[col] = dbKey;
        break;
      }
    }
  });
  return mapping;
}

// ─── Parse CSV text ───────────────────────────────────────────────────────────

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const result = [];
    let cur = '', inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuote = !inQuote; }
      else if (ch === ',' && !inQuote) { result.push(cur); cur = ''; }
      else { cur += ch; }
    }
    result.push(cur);
    return result;
  };

  const headers = parseRow(lines[0]).map(h => h.trim().replace(/^"|"$/g, ''));
  const rows = lines.slice(1).filter(l => l.trim()).map(line => {
    const vals = parseRow(line).map(v => v.trim().replace(/^"|"$/g, ''));
    const row = {};
    headers.forEach((h, i) => { row[h] = vals[i] ?? ''; });
    return row;
  });
  return { headers, rows };
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ImportWizard() {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState([]);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [entity, setEntity] = useState(null);
  const [mapping, setMapping] = useState({});         // {csvCol: dbField}
  const [draggingCol, setDraggingCol] = useState(null);
  const [duplicateMode, setDuplicateMode] = useState('skip');
  const [importing, setImporting] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [result, setResult] = useState(null);
  const fileInputRef = useRef();

  // ── Step 1: File handling ──────────────────────────────────────────────────

  const processFile = useCallback(async (f) => {
    if (!f) return;

    const ext = f.name.split('.').pop().toLowerCase();

    if (ext === 'csv') {
      const text = await f.text();
      const { headers: h, rows: r } = parseCSV(text);
      setFile(f); setHeaders(h); setRows(r);
      setPreview(r.slice(0, 5));
    } else if (ext === 'xlsx' || ext === 'xls') {
      try {
        // Dynamically import SheetJS
        const XLSX = await import('xlsx');
        const ab = await f.arrayBuffer();
        const wb = XLSX.read(ab, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!jsonData.length) { alert('Excel dosyası boş görünüyor.'); return; }
        const h = Object.keys(jsonData[0]);
        setFile(f); setHeaders(h); setRows(jsonData);
        setPreview(jsonData.slice(0, 5));
      } catch (e) {
        alert('Excel dosyası okunamadı. xlsx paketi yüklü olduğundan emin olun. Hata: ' + e.message);
      }
    } else {
      alert('Sadece .csv, .xlsx veya .xls dosyaları desteklenir.');
    }
  }, []);

  const onFileDrop = useCallback((e) => {
    e.preventDefault(); setIsDraggingFile(false);
    const f = e.dataTransfer.files[0];
    if (f) processFile(f);
  }, [processFile]);

  const onFileInput = (e) => {
    const f = e.target.files[0];
    if (f) processFile(f);
  };

  // ── Step 2: Entity selection ───────────────────────────────────────────────

  const handleEntitySelect = (e) => {
    setEntity(e);
    // Auto-match columns
    setMapping(autoMatch(headers, e.fields));
    setStep(3);
  };

  // ── Step 3: Field mapping (drag & drop) ───────────────────────────────────

  const handleMapDrop = (dbField) => {
    if (!draggingCol) return;
    // Remove any existing mapping to this dbField
    const cleaned = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v !== dbField));
    setMapping({ ...cleaned, [draggingCol]: dbField });
    setDraggingCol(null);
  };

  const removeMapping = (csvCol) => {
    const m = { ...mapping };
    delete m[csvCol];
    setMapping(m);
  };

  const getMappedCol = (dbField) => Object.entries(mapping).find(([, v]) => v === dbField)?.[0] || null;

  const unmappedCols = headers.filter(h => !mapping[h]);

  const requiredFields = entity?.fields.filter(f => f.required) || [];
  const missingRequired = requiredFields.filter(f => !getMappedCol(f.key));

  // ── Step 4: Execute import ─────────────────────────────────────────────────

  const handleImport = async () => {
    setImporting(true);
    setProgressText('');
    setResult(null);

    const batchSize = 100;
    let successCount = 0;
    let skippedCount = 0;
    let failedCount = 0;
    let errorsList = [];

    try {
      for (let i = 0; i < rows.length; i += batchSize) {
        const chunk = rows.slice(i, i + batchSize);
        const percent = Math.round((i / rows.length) * 100);
        setProgressText(`Aktarılıyor: %${percent} (${i}/${rows.length})`);

        const { data } = await axios.post('/api/import/execute', {
          entity: entity.id,
          mapping,
          rows: chunk,
          duplicate_mode: duplicateMode,
        }, { headers: HEADERS() });

        successCount += data.success || 0;
        skippedCount += data.skipped || 0;
        failedCount += data.failed || 0;
        if (data.errors) {
          errorsList = [...errorsList, ...data.errors];
        }
      }

      setResult({
        success: successCount,
        skipped: skippedCount,
        failed: failedCount,
        errors: errorsList
      });
      setStep(4);
    } catch (e) {
      alert('İçe aktarma hatası: ' + (e.response?.data?.error || e.message));
    } finally {
      setImporting(false);
      setProgressText('');
    }
  };

  const reset = () => {
    setStep(1); setFile(null); setHeaders([]); setRows([]);
    setPreview([]); setEntity(null); setMapping({});
    setDraggingCol(null); setResult(null); setDuplicateMode('skip');
  };

  const downloadErrors = () => {
    if (!result?.errors?.length) return;
    const blob = new Blob([result.errors.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'import_errors.txt';
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="iw-container">
      {/* ── Step Indicator ── */}
      <div className="iw-steps">
        {['Dosya Yükle', 'Hedef Seç', 'Alan Eşleştir', 'Aktar'].map((label, idx) => {
          const n = idx + 1;
          const done = step > n;
          const active = step === n;
          return (
            <div key={n} className="iw-step-item">
              <div className={`iw-step-circle ${active ? 'active' : ''} ${done ? 'done' : ''}`}>
                {done ? '✓' : n}
              </div>
              <span className={`iw-step-label ${active ? 'active' : ''}`}>{label}</span>
              {n < 4 && <div className={`iw-step-line ${done ? 'done' : ''}`} />}
            </div>
          );
        })}
      </div>

      {/* ══════════════════ STEP 1 — File Upload ══════════════════ */}
      {step === 1 && (
        <div className="iw-card">
          <h3 className="iw-card-title">📂 Dosya Yükleyin</h3>
          <p className="iw-card-desc">CSV veya Excel dosyanızı aşağıya sürükleyin ya da seçin.</p>

          <div
            className={`iw-dropzone ${isDraggingFile ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={onFileDrop}
            onClick={() => !file && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={onFileInput}
            />
            {file ? (
              <div className="iw-file-info">
                <div className="iw-file-icon">📄</div>
                <div>
                  <div className="iw-file-name">{file.name}</div>
                  <div className="iw-file-meta">
                    {rows.length} satır · {headers.length} sütun · {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <button className="iw-btn-ghost" onClick={(e) => { e.stopPropagation(); reset(); }}>✕</button>
              </div>
            ) : (
              <div className="iw-dropzone-inner">
                <div className="iw-drop-icon">{isDraggingFile ? '📥' : '☁️'}</div>
                <p className="iw-drop-text">{isDraggingFile ? 'Bırakın!' : 'Dosyayı buraya sürükleyin'}</p>
                <p className="iw-drop-sub">ya da tıklayarak seçin · CSV · XLSX · XLS</p>
              </div>
            )}
          </div>

          {preview.length > 0 && (
            <div className="iw-preview-wrap">
              <p className="iw-preview-label">Önizleme (ilk {preview.length} satır)</p>
              <div className="iw-preview-scroll">
                <table className="iw-preview-table">
                  <thead>
                    <tr>{headers.map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.map((r, i) => (
                      <tr key={i}>{headers.map(h => <td key={h}>{r[h]}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="iw-actions">
            <button
              className="iw-btn-primary"
              disabled={!file || rows.length === 0}
              onClick={() => setStep(2)}
            >
              Devam Et →
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════ STEP 2 — Entity Selection ══════════════════ */}
      {step === 2 && (
        <div className="iw-card">
          <h3 className="iw-card-title">🎯 Hedef Seçin</h3>
          <p className="iw-card-desc">Verilerin aktarılacağı bölümü seçin.</p>

          <div className="iw-entity-grid">
            {ENTITIES.map(e => (
              <button
                key={e.id}
                className="iw-entity-card"
                style={{ '--ec': e.color }}
                onClick={() => handleEntitySelect(e)}
              >
                <span className="iw-entity-icon">{e.icon}</span>
                <span className="iw-entity-label">{e.label}</span>
                <span className="iw-entity-desc">{e.desc}</span>
              </button>
            ))}
          </div>

          <div className="iw-actions">
            <button className="iw-btn-ghost" onClick={() => setStep(1)}>← Geri</button>
          </div>
        </div>
      )}

      {/* ══════════════════ STEP 3 — Field Mapping ══════════════════ */}
      {step === 3 && entity && (
        <div className="iw-card">
          <div className="iw-mapping-header">
            <div>
              <h3 className="iw-card-title">🔗 Alan Eşleştirme</h3>
              <p className="iw-card-desc">
                CSV sütunlarını hedef alanlara sürükleyip bırakın.
                <span className="iw-badge-required"> Kırmızı</span> = Zorunlu alan.
              </p>
            </div>
            <button
              className="iw-btn-auto"
              onClick={() => setMapping(autoMatch(headers, entity.fields))}
            >
              ⚡ Otomatik Eşleştir
            </button>
          </div>

          <div className="iw-mapping-panels">
            {/* LEFT — CSV Columns */}
            <div className="iw-panel">
              <div className="iw-panel-title">📋 CSV Sütunları ({headers.length})</div>
              <div className="iw-col-list">
                {headers.map(col => {
                  const isMapped = !!mapping[col];
                  return (
                    <div
                      key={col}
                      className={`iw-col-chip ${isMapped ? 'mapped' : ''} ${draggingCol === col ? 'dragging' : ''}`}
                      draggable
                      onDragStart={() => setDraggingCol(col)}
                      onDragEnd={() => setDraggingCol(null)}
                    >
                      <span className="iw-col-name">{col}</span>
                      {isMapped && (
                        <span className="iw-col-tag">→ {mapping[col]}</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {unmappedCols.length > 0 && (
                <p className="iw-unmapped-note">
                  ℹ️ {unmappedCols.length} sütun eşleştirilmedi (aktarılmayacak)
                </p>
              )}
            </div>

            {/* Arrow */}
            <div className="iw-panel-arrow">→</div>

            {/* RIGHT — DB Fields */}
            <div className="iw-panel">
              <div className="iw-panel-title">🗄️ Veritabanı Alanları ({entity.label})</div>
              <div className="iw-field-list">
                {entity.fields.map(f => {
                  const mappedCol = getMappedCol(f.key);
                  const isDropTarget = draggingCol !== null;
                  return (
                    <div
                      key={f.key}
                      className={`iw-field-row ${mappedCol ? 'field-matched' : ''} ${isDropTarget && !mappedCol ? 'drop-ready' : ''} ${f.required && !mappedCol ? 'field-missing' : ''}`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleMapDrop(f.key)}
                    >
                      <div className="iw-field-info">
                        <span className="iw-field-key">{f.key}</span>
                        <span className="iw-field-label">{f.label}</span>
                        {f.required && <span className="iw-badge-req">Zorunlu</span>}
                      </div>
                      {mappedCol ? (
                        <div className="iw-field-match">
                          <span className="iw-match-chip">{mappedCol}</span>
                          <button className="iw-remove-btn" onClick={() => removeMapping(mappedCol)}>✕</button>
                        </div>
                      ) : (
                        <div className="iw-field-empty">
                          {isDropTarget ? 'Bırak' : 'Eşleştirilmedi'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {missingRequired.length > 0 && (
            <div className="iw-warning-box">
              ⚠️ Zorunlu alanlar eşleştirilmedi: <strong>{missingRequired.map(f => f.label).join(', ')}</strong>
            </div>
          )}

          {/* Duplicate mode */}
          <div className="iw-dup-section">
            <span className="iw-dup-label">Duplicate kayıt davranışı:</span>
            <div className="iw-dup-options">
              {[
                { id: 'skip', label: '⏭ Atla', desc: 'Mevcut kaydı değiştirme' },
                { id: 'update', label: '♻️ Güncelle', desc: 'Mevcut kaydı güncelle' },
                { id: 'create', label: '➕ Oluştur', desc: 'Yeni kayıt olarak ekle' },
              ].map(opt => (
                <label key={opt.id} className={`iw-dup-option ${duplicateMode === opt.id ? 'selected' : ''}`}>
                  <input type="radio" name="dupmode" value={opt.id} checked={duplicateMode === opt.id}
                    onChange={() => setDuplicateMode(opt.id)} style={{ display: 'none' }} />
                  <span className="iw-dup-opt-label">{opt.label}</span>
                  <span className="iw-dup-opt-desc">{opt.desc}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="iw-actions">
            <button className="iw-btn-ghost" onClick={() => setStep(2)}>← Geri</button>
            <button
              className="iw-btn-primary"
              disabled={missingRequired.length > 0 || importing}
              onClick={handleImport}
            >
              {importing ? (
                <span className="iw-loading">
                  <span className="iw-spinner" /> {progressText || 'Aktarılıyor...'}
                </span>
              ) : `📤 ${rows.length} Kaydı Aktar`}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════ STEP 4 — Result ══════════════════ */}
      {step === 4 && result && (
        <div className="iw-card">
          <h3 className="iw-card-title">🎉 Aktarım Tamamlandı</h3>

          <div className="iw-result-cards">
            <div className="iw-result-item success">
              <div className="iw-result-num">{result.success}</div>
              <div className="iw-result-lbl">✅ Başarılı</div>
            </div>
            <div className="iw-result-item skipped">
              <div className="iw-result-num">{result.skipped}</div>
              <div className="iw-result-lbl">⏭ Atlandı</div>
            </div>
            <div className="iw-result-item failed">
              <div className="iw-result-num">{result.failed}</div>
              <div className="iw-result-lbl">❌ Başarısız</div>
            </div>
          </div>

          {result.errors?.length > 0 && (
            <div className="iw-errors-wrap">
              <div className="iw-errors-header">
                <span>Hata detayları ({result.errors.length})</span>
                <button className="iw-btn-ghost-sm" onClick={downloadErrors}>⬇ İndir</button>
              </div>
              <div className="iw-errors-list">
                {result.errors.slice(0, 10).map((e, i) => (
                  <div key={i} className="iw-error-line">{e}</div>
                ))}
                {result.errors.length > 10 && (
                  <div className="iw-error-more">... ve {result.errors.length - 10} hata daha</div>
                )}
              </div>
            </div>
          )}

          <div className="iw-actions">
            <button className="iw-btn-primary" onClick={reset}>🔄 Yeni Aktarım</button>
          </div>
        </div>
      )}

      {/* ── Styles ─────────────────────────────────────────────────────────── */}
      <style>{`
        .iw-container { font-family: inherit; }

        /* ── Step Indicator ── */
        .iw-steps {
          display: flex;
          align-items: center;
          gap: 0;
          margin-bottom: 28px;
          padding: 0 8px;
        }
        .iw-step-item {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
        }
        .iw-step-item:last-child { flex: 0; }
        .iw-step-circle {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 700;
          flex-shrink: 0;
          background: var(--bg-sub);
          color: #64748b;
          border: 2px solid var(--border);
          transition: all 0.3s ease;
        }
        .iw-step-circle.active {
          background: linear-gradient(135deg, var(--tertiary), var(--secondary));
          color: white;
          border-color: transparent;
          box-shadow: 0 4px 12px rgba(var(--tertiary-rgb), 0.35);
        }
        .iw-step-circle.done {
          background: #10b981;
          color: white;
          border-color: transparent;
        }
        .iw-step-label {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          white-space: nowrap;
        }
        .iw-step-label.active { color: var(--tertiary); }
        .iw-step-line {
          flex: 1;
          height: 2px;
          background: var(--border);
          margin: 0 6px;
          transition: background 0.3s;
        }
        .iw-step-line.done { background: #10b981; }

        /* ── Card ── */
        .iw-card {
          background: rgba(12, 15, 25, 0.6);
          border: 1px solid var(--border);
          border-radius: 18px;
          padding: 28px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .iw-card-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--text);
          margin: 0;
        }
        .iw-card-desc {
          font-size: 13px;
          color: #64748b;
          margin: -12px 0 0;
        }

        /* ── Dropzone ── */
        .iw-dropzone {
          border: 2px dashed var(--border-hover);
          border-radius: 16px;
          padding: 40px 20px;
          text-align: center;
          cursor: pointer;
          transition: all 0.25s ease;
          background: var(--bg-sub);
          position: relative;
          overflow: hidden;
        }
        .iw-dropzone::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, rgba(var(--tertiary-rgb), 0.05), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .iw-dropzone:hover::before,
        .iw-dropzone.drag-over::before { opacity: 1; }
        .iw-dropzone.drag-over {
          border-color: var(--tertiary);
          background: rgba(var(--tertiary-rgb), 0.06);
          transform: scale(1.005);
        }
        .iw-dropzone.has-file {
          border-style: solid;
          border-color: rgba(16, 185, 129, 0.4);
          background: rgba(16, 185, 129, 0.04);
          cursor: default;
        }
        .iw-dropzone-inner { display: flex; flex-direction: column; align-items: center; gap: 8px; }
        .iw-drop-icon { font-size: 44px; line-height: 1; }
        .iw-drop-text { font-size: 16px; font-weight: 600; color: var(--text); margin: 0; }
        .iw-drop-sub { font-size: 12px; color: #94a3b8; margin: 0; }
        .iw-file-info {
          display: flex;
          align-items: center;
          gap: 16px;
          justify-content: center;
        }
        .iw-file-icon { font-size: 32px; }
        .iw-file-name { font-weight: 700; color: var(--text); font-size: 14px; }
        .iw-file-meta { font-size: 12px; color: #64748b; margin-top: 2px; }

        /* ── Preview Table ── */
        .iw-preview-wrap { display: flex; flex-direction: column; gap: 6px; }
        .iw-preview-label { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin: 0; }
        .iw-preview-scroll { overflow-x: auto; border-radius: 12px; border: 1px solid var(--border); }
        .iw-preview-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .iw-preview-table th {
          background: rgba(var(--tertiary-rgb), 0.08);
          color: var(--secondary);
          font-weight: 700;
          padding: 8px 12px;
          text-align: left;
          white-space: nowrap;
          border-bottom: 1px solid var(--border);
        }
        .iw-preview-table td {
          padding: 7px 12px;
          color: var(--text);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          white-space: nowrap;
          max-width: 160px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .iw-preview-table tr:last-child td { border-bottom: none; }

        /* ── Entity Grid ── */
        .iw-entity-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px;
        }
        .iw-entity-card {
          background: var(--bg-sub);
          border: 2px solid var(--border);
          border-radius: 16px;
          padding: 22px 18px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 6px;
          text-align: left;
        }
        .iw-entity-card:hover {
          border-color: var(--ec, var(--tertiary));
          background: color-mix(in srgb, var(--ec, var(--tertiary)) 8%, transparent);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.2);
        }
        .iw-entity-icon { font-size: 28px; }
        .iw-entity-label { font-size: 15px; font-weight: 700; color: var(--text); }
        .iw-entity-desc { font-size: 12px; color: #64748b; }

        /* ── Mapping ── */
        .iw-mapping-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
        }
        .iw-mapping-panels {
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }
        .iw-panel {
          flex: 1;
          background: var(--bg-sub);
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .iw-panel-title {
          font-size: 12px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid var(--border);
          padding-bottom: 8px;
        }
        .iw-panel-arrow {
          font-size: 24px;
          color: #4b5563;
          align-self: center;
          flex-shrink: 0;
        }

        /* CSV Column Chips */
        .iw-col-list { display: flex; flex-direction: column; gap: 6px; }
        .iw-col-chip {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          border-radius: 8px;
          background: rgba(var(--tertiary-rgb), 0.06);
          border: 1px solid rgba(var(--tertiary-rgb), 0.15);
          cursor: grab;
          font-size: 13px;
          transition: all 0.15s;
          gap: 8px;
        }
        .iw-col-chip:hover { border-color: var(--tertiary); background: rgba(var(--tertiary-rgb), 0.12); }
        .iw-col-chip.mapped { opacity: 0.55; }
        .iw-col-chip.dragging { opacity: 0.3; border-style: dashed; }
        .iw-col-name { font-weight: 600; color: var(--text); flex: 1; }
        .iw-col-tag { font-size: 10px; color: var(--secondary); background: rgba(var(--secondary-rgb),0.12); padding: 2px 6px; border-radius: 4px; font-weight: 600; white-space: nowrap; }
        .iw-unmapped-note { font-size: 11px; color: #94a3b8; margin: 0; }

        /* DB Field Rows */
        .iw-field-list { display: flex; flex-direction: column; gap: 6px; }
        .iw-field-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 10px;
          border: 2px dashed var(--border);
          background: var(--bg-card);
          transition: all 0.15s;
          min-height: 48px;
        }
        .iw-field-row.drop-ready { border-color: rgba(var(--tertiary-rgb), 0.4); background: rgba(var(--tertiary-rgb),0.04); }
        .iw-field-row.field-matched {
          border-style: solid;
          border-color: rgba(16, 185, 129, 0.4);
          background: rgba(16, 185, 129, 0.05);
        }
        .iw-field-row.field-missing { border-color: rgba(244, 63, 94, 0.35); }
        .iw-field-info { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
        .iw-field-key { font-family: monospace; font-size: 11px; color: var(--secondary); flex-shrink: 0; }
        .iw-field-label { font-size: 12px; font-weight: 600; color: var(--text); }
        .iw-badge-req {
          font-size: 9px;
          font-weight: 700;
          color: #f43f5e;
          background: rgba(244, 63, 94, 0.12);
          padding: 2px 5px;
          border-radius: 4px;
          flex-shrink: 0;
        }
        .iw-badge-required { font-size: 12px; color: #f43f5e; }
        .iw-field-match { display: flex; align-items: center; gap: 6px; }
        .iw-match-chip { font-size: 11px; background: rgba(16, 185, 129, 0.15); color: #10b981; padding: 3px 8px; border-radius: 6px; font-weight: 600; }
        .iw-remove-btn { background: none; border: none; color: #94a3b8; cursor: pointer; font-size: 14px; padding: 0 2px; line-height: 1; }
        .iw-remove-btn:hover { color: #ef4444; }
        .iw-field-empty { font-size: 11px; color: #475569; }

        /* Duplicate mode */
        .iw-dup-section {
          background: var(--bg-sub);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .iw-dup-label { font-size: 13px; font-weight: 700; color: var(--text); }
        .iw-dup-options { display: flex; gap: 10px; }
        .iw-dup-option {
          flex: 1;
          border: 2px solid var(--border);
          border-radius: 10px;
          padding: 10px 14px;
          cursor: pointer;
          transition: all 0.15s;
          display: flex;
          flex-direction: column;
          gap: 3px;
          background: var(--bg-card);
        }
        .iw-dup-option.selected {
          border-color: var(--tertiary);
          background: rgba(var(--tertiary-rgb),0.08);
        }
        .iw-dup-opt-label { font-size: 13px; font-weight: 700; color: var(--text); }
        .iw-dup-opt-desc { font-size: 11px; color: #64748b; }

        /* Warnings */
        .iw-warning-box {
          background: rgba(251, 191, 36, 0.08);
          border: 1px solid rgba(251, 191, 36, 0.25);
          border-radius: 10px;
          padding: 12px 16px;
          font-size: 13px;
          color: #fbbf24;
        }

        /* Buttons */
        .iw-actions { display: flex; justify-content: flex-end; gap: 10px; }
        .iw-btn-primary {
          background: linear-gradient(135deg, var(--tertiary), var(--secondary));
          color: white;
          border: none;
          border-radius: 10px;
          padding: 11px 24px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 12px rgba(var(--tertiary-rgb),0.25);
        }
        .iw-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(var(--tertiary-rgb),0.35); }
        .iw-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }
        .iw-btn-ghost {
          background: transparent;
          border: 1px solid var(--border-hover);
          color: #94a3b8;
          border-radius: 10px;
          padding: 10px 20px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
        }
        .iw-btn-ghost:hover { color: var(--text); border-color: var(--text-muted); }
        .iw-btn-auto {
          background: rgba(var(--tertiary-rgb),0.1);
          border: 1px solid rgba(var(--tertiary-rgb),0.25);
          color: var(--secondary);
          border-radius: 8px;
          padding: 8px 14px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.15s;
          flex-shrink: 0;
        }
        .iw-btn-auto:hover { background: rgba(var(--tertiary-rgb),0.18); }
        .iw-btn-ghost-sm {
          background: none;
          border: 1px solid var(--border-hover);
          color: #94a3b8;
          border-radius: 6px;
          padding: 3px 10px;
          font-size: 11px;
          cursor: pointer;
        }

        /* Loading spinner */
        .iw-loading { display: flex; align-items: center; gap: 8px; }
        .iw-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: iw-spin 0.6s linear infinite;
          display: inline-block;
        }
        @keyframes iw-spin { to { transform: rotate(360deg); } }

        /* Result */
        .iw-result-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
        }
        .iw-result-item {
          border-radius: 14px;
          padding: 24px 16px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .iw-result-item.success { background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.2); }
        .iw-result-item.skipped { background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.2); }
        .iw-result-item.failed { background: rgba(244,63,94,0.08); border: 1px solid rgba(244,63,94,0.2); }
        .iw-result-num { font-size: 40px; font-weight: 800; color: var(--text); line-height: 1; }
        .iw-result-item.success .iw-result-num { color: #10b981; }
        .iw-result-item.skipped .iw-result-num { color: #f59e0b; }
        .iw-result-item.failed .iw-result-num { color: #f43f5e; }
        .iw-result-lbl { font-size: 13px; font-weight: 600; color: #94a3b8; }

        /* Errors */
        .iw-errors-wrap {
          background: rgba(244,63,94,0.05);
          border: 1px solid rgba(244,63,94,0.2);
          border-radius: 12px;
          overflow: hidden;
        }
        .iw-errors-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          background: rgba(244,63,94,0.08);
          font-size: 12px;
          font-weight: 700;
          color: #f43f5e;
        }
        .iw-errors-list {
          max-height: 160px;
          overflow-y: auto;
          padding: 8px 14px;
        }
        .iw-error-line {
          font-size: 12px;
          color: #fb7185;
          padding: 4px 0;
          border-bottom: 1px solid rgba(244,63,94,0.08);
          font-family: monospace;
        }
        .iw-error-line:last-child { border-bottom: none; }
        .iw-error-more { font-size: 11px; color: #94a3b8; padding: 6px 0; }
      `}</style>
    </div>
  );
}
